import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';

// Helper per sessionStorage
const markSessionVerifiedInStorage = (sessionId) => sessionStorage.setItem(`verified_session_${sessionId}`, 'true');
const isSessionVerifiedInStorage = (sessionId) => sessionStorage.getItem(`verified_session_${sessionId}`) === 'true';
const clearSessionVerifiedInStorage = (sessionId) => sessionStorage.removeItem(`verified_session_${sessionId}`);

// Variabili di colore (idealmente da un file di tema globale, ma qui per esempio)
const darkPurple = '#1a0c2e'; // Colore di sfondo scuro principale
const accentPink = '#ff40c0'; // Rosa acceso per titoli, icone
const softPinkGlow = 'rgba(255, 64, 192, 0.1)';
const cardBackground = 'rgba(27, 12, 46, 0.75)'; // Sfondo card più scuro e più opaco
const cardBorder = 'rgba(255, 64, 192, 0.3)';
const textLight = '#f0f0f0';
const textMedium = 'rgba(240, 240, 240, 0.7)';
const buttonGoldStart = '#FFD700';
const buttonGoldEnd = '#FFA500';
const buttonTextDark = '#1a0c2e';

const PageContainer = styled.div`
  min-height: 100vh; // Assicura che copra l'intera altezza
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: ${darkPurple};
  background-image: radial-gradient(ellipse at top left, rgba(255,64,192,0.3) 0%, transparent 40%),
                    radial-gradient(ellipse at bottom right, rgba(120,0,255,0.3) 0%, transparent 40%);
  font-family: 'Poppins', sans-serif;
  color: ${textLight};
`;

const ContentCardStyled = styled.div`
  background-color: ${cardBackground};
  border-radius: 20px;
  padding: 30px 40px;
  margin: 20px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.2), 0 0 20px ${softPinkGlow};
  border: 1px solid ${cardBorder};
  text-align: center;
  backdrop-filter: blur(10px); // Effetto vetro smerigliato
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const IconStyled = styled.div`
  font-size: 4.5rem; // Leggermente più piccolo per non essere eccessivo
  color: ${accentPink};
  margin-bottom: 25px;
  animation: ${pulseAnimation} 2s infinite ease-in-out;
`;

const TitleStyled = styled.h1`
  font-family: 'Poppins', sans-serif; // Manteniamo Poppins per coerenza
  font-size: 2.2rem; // Adattato per lo spazio della card
  font-weight: 700;
  color: ${accentPink};
  margin-bottom: 15px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const MessageStyled = styled.p`
  font-size: 1.1rem;
  color: ${textLight};
  max-width: 500px;
  margin: 0 auto 25px auto; // Centrato se più stretto della card
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const RedirectMsgStyled = styled.p`
  font-size: 0.85rem;
  color: ${textMedium};
  font-style: italic;
  margin-top: 30px;
`;

const PlanDetailsStyled = styled.div`
  background-color: rgba(0,0,0, 0.2); // Più scuro e integrato
  border-radius: 12px;
  padding: 20px;
  margin: 30px 0;
  border: 1px solid rgba(255, 64, 192, 0.15);
  max-width: 450px; // Leggermente più stretto della card
  width: 90%;
  margin-left: auto;
  margin-right: auto;
  
  h3 {
    color: ${accentPink};
    margin-bottom: 15px;
    font-size: 1.3rem;
    font-weight: 600;
  }
  
  ul {
    list-style-type: none;
    padding: 0;
    text-align: left;
    
    li {
      display: flex;
      justify-content: space-between;
      padding: 12px 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      font-size: 0.95rem;
      
      &:last-child {
        border-bottom: none;
      }
      
      span.label {
        color: ${textMedium};
      }
      
      span.value {
        font-weight: 600;
        color: ${textLight};
      }
    }
  }
`;

const ButtonStyled = styled.button`
  background: linear-gradient(45deg, ${buttonGoldStart}, ${buttonGoldEnd});
  color: ${buttonTextDark};
  border: none;
  border-radius: 50px; // Molto arrotondato
  padding: 14px 35px;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 25px;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0,0,0, 0.2), 0 2px 8px rgba(255,215,0,0.3);
  
  &:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow: 0 8px 25px rgba(0,0,0, 0.25), 0 4px 12px rgba(255,215,0,0.4);
  }
  
  i {
    margin-left: 8px;
  }
`;

const getPlanDetails = (planId) => {
  const plans = {
    'basic': { credits: 500, analyses: 2, price: '€4.99' },
    'standard': { credits: 1500, analyses: 6, price: '€9.99' },
    'premium': { credits: 3000, analyses: 12, price: '€14.99' }
  };
  
  return plans[planId] || { credits: 0, analyses: 0, price: 'Sconosciuto' };
};

const getPlanName = (planId) => {
  const names = {
    'basic': 'Basic',
    'standard': 'Standard',
    'premium': 'Premium'
  };
  
  return names[planId] || (planId ? planId.charAt(0).toUpperCase() + planId.slice(1) : 'Unknown');
};

const PaymentSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchUser, currentUser } = useAuth();
  
  const [internalSessionId, setInternalSessionId] = useState(null);
  const [internalPlanId, setInternalPlanId] = useState(null);

  const [verificationState, setVerificationState] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  const hasInitiatedVerificationForCurrentMount = useRef(false); // Rinominato per chiarezza

  const planDetails = internalPlanId ? getPlanDetails(internalPlanId) : getPlanDetails(null);
  const planName = internalPlanId ? getPlanName(internalPlanId) : getPlanName(null);

  useEffect(() => {
    console.log("PaymentSuccessPage: Effect 1 (URL Parsing) - Start. location.search:", location.search, "Current state:", verificationState);
    const params = new URLSearchParams(location.search);
    const sessionIdFromUrl = params.get('session_id');
    const planIdFromUrl = params.get('plan');

    if (sessionIdFromUrl && planIdFromUrl) {
      if (isSessionVerifiedInStorage(sessionIdFromUrl)) {
        console.log(`PaymentSuccessPage: Session ${sessionIdFromUrl} already verified in sessionStorage. Setting state to 'success'.`);
        setInternalSessionId(sessionIdFromUrl);
        setInternalPlanId(planIdFromUrl);
        setVerificationState('success'); 
      } else if (verificationState === 'idle') {
        console.log(`PaymentSuccessPage: Session ${sessionIdFromUrl} not yet verified in sessionStorage and state is 'idle'. Setting up for verification.`);
        setInternalSessionId(sessionIdFromUrl);
        setInternalPlanId(planIdFromUrl);
        setVerificationState('verifying');
      } else {
        console.log(`PaymentSuccessPage: Effect 1 - Skipping parsing logic. SessionID: ${sessionIdFromUrl}, State: ${verificationState}`);
      }
    } else {
      console.error('PaymentSuccessPage: Missing session_id or plan_id in URL.');
      setErrorMessage('Missing payment session information in URL.');
      setVerificationState('error');
    }
  }, [location.search, verificationState]);

  useEffect(() => {
    console.log(`PaymentSuccessPage: Effect 2 (Verification) - Start. internalSessionId: ${internalSessionId}, internalPlanId: ${internalPlanId}, verificationState: ${verificationState}, hasInitiated: ${hasInitiatedVerificationForCurrentMount.current}`);
    if (!internalSessionId || !internalPlanId) {
        console.log("PaymentSuccessPage: Effect 2 - Skipping. Session/Plan ID not set yet.");
        return;
    }
    if (verificationState !== 'verifying') {
        console.log("PaymentSuccessPage: Effect 2 - Skipping. State is not 'verifying'. Current state:", verificationState);
        return; 
    }
    if (hasInitiatedVerificationForCurrentMount.current) {
      console.log("PaymentSuccessPage: Effect 2 - Skipping. Verification already initiated for this mount.");
      return;
    }

    const verifyPayment = async () => {
      hasInitiatedVerificationForCurrentMount.current = true;
      console.log(`PaymentSuccessPage: Verifying session ${internalSessionId} for plan ${internalPlanId}`);
        
      try {
        const response = await apiService.client.get(`/verify-checkout-session/${internalSessionId}`);
        console.log('PaymentSuccessPage: Verification API response:', response.data);

        if (response.data && response.data.payment_status === 'paid') {
          console.log('PaymentSuccessPage: Payment confirmed. Fetching user data.');
          await fetchUser(); 
          markSessionVerifiedInStorage(internalSessionId);
          setVerificationState('success');
          console.log('PaymentSuccessPage: User data fetched, verification successful.');
        } else {
          const errorDetail = response.data?.message || 'Payment verification failed or payment not completed.';
          console.error('PaymentSuccessPage: Payment verification failed.', errorDetail, response.data);
          setErrorMessage(errorDetail);
          setVerificationState('error');
        }
      } catch (err) {
        console.error('PaymentSuccessPage: Error during API call or user fetch:', err);
        const apiError = err.response?.data?.message || err.response?.data?.error || err.message;
        setErrorMessage(apiError || 'Unexpected error during verification.');
        setVerificationState('error');
      }
    };

    console.log("PaymentSuccessPage: Effect 2 - Conditions met, calling verifyPayment().");
    verifyPayment();

  }, [internalSessionId, internalPlanId, fetchUser, verificationState]);

  useEffect(() => {
    console.log(`PaymentSuccessPage: Effect 3 (Redirect) - Start. verificationState: ${verificationState}, currentUser: ${!!currentUser}`);
    let timerId = null;
    if (verificationState === 'success' && currentUser) {
      console.log('PaymentSuccessPage: Effect 3 - State is "success" and currentUser is available. Setting timer for redirect.');
      const currentPlanDetails = getPlanDetails(internalPlanId); 
      timerId = setTimeout(() => {
        console.log('PaymentSuccessPage: Redirecting to dashboard.');
        navigate(`/dashboard?from=payment&plan=${internalPlanId}&creditsAdded=${currentPlanDetails.credits}&status=success`);
      }, 4000);
    } else {
        console.log(`PaymentSuccessPage: Effect 3 - Conditions not met. VerificationState: ${verificationState}, currentUser available: ${!!currentUser}`);
    }

    return () => {
      if (timerId) {
        clearTimeout(timerId);
        console.log("PaymentSuccessPage: Effect 3 - Timer cleared from cleanup.");
      }
    };
  }, [verificationState, currentUser, navigate, internalPlanId]);

  if (verificationState === 'idle' || verificationState === 'verifying') {
    return (
      <PageContainer>
        <ContentCardStyled>
          <TitleStyled>Verifying Payment...</TitleStyled>
          <MessageStyled>We are confirming your purchase details. Please wait...</MessageStyled>
          <IconStyled>
            <i className="fas fa-spinner fa-spin"></i>
          </IconStyled>
        </ContentCardStyled>
      </PageContainer>
    );
  }

  if (verificationState === 'error') {
    return (
      <PageContainer>
        <ContentCardStyled>
          <IconStyled><i className="fas fa-exclamation-triangle"></i></IconStyled>
          <TitleStyled>Verification Error</TitleStyled>
          <MessageStyled>
            An error occurred: {errorMessage}
          </MessageStyled>
          <ButtonStyled onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </ButtonStyled>
        </ContentCardStyled>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentCardStyled>
        <IconStyled><i className="fas fa-check-circle"></i></IconStyled>
        <TitleStyled>Payment Successful!</TitleStyled>
        <MessageStyled>
          Thank you for your purchase. Credits have been added to your account and are ready to use.
        </MessageStyled>
        <PlanDetailsStyled>
          <h3>Purchase Summary</h3>
          <ul>
            <li><span className="label">Plan</span><span className="value">{planName}</span></li>
            <li><span className="label">Credits Added</span><span className="value">{planDetails.credits}</span></li>
            <li><span className="label">Analyses Available</span><span className="value">{planDetails.analyses}</span></li>
            <li><span className="label">Amount Paid</span><span className="value">{planDetails.price}</span></li>
          </ul>
        </PlanDetailsStyled>
        <ButtonStyled onClick={() => navigate(`/dashboard?from=payment&plan=${internalPlanId}&creditsAdded=${planDetails.credits}&status=success`)}>
          Go to Dashboard <i className="fas fa-arrow-right"></i>
        </ButtonStyled>
        <RedirectMsgStyled>You will be automatically redirected to the dashboard in a few seconds...</RedirectMsgStyled>
      </ContentCardStyled>
    </PageContainer>
  );
};

export default PaymentSuccessPage; 